<template>
  <div class="container">
    <div class="row">
      <div class="page-body">

        <LoadingWidgetReasons v-if="!bookSuccess && bookLoading" :title="$t('confirmingYourHotelReservation')" :reasons="widgetReasons" />

        <div class="book-body">
          <div class="book-col">
            <div class="form-notification error" v-if="showBookFailedMsg">
                <i class="fal fa-exclamation-circle"></i>
                <div class="message">
                    <h4>{{ $t('bookingFailed') }}</h4>
                    <p>{{ $t('bookingFailedErrorParagraph') }}</p>
                </div>
            </div>

            <div v-if="!bookSuccess && !bookLoading" class="hotel-booking-form">
              <h1>{{ $t('bookYourHotelStay') }}:</h1>

              <HotelListing
                v-if="hotelData"
                :hidePrice="true"
                :hotelData="hotelData"
                :cancelPolicy="hotelData.room_data[0].rate_data[0].cancellation_details[0].description"
                :isBookPage="true" />

              <LoadingHotelListing :hidePrice="true" v-else />

              <section class="hotel-booking-section">
                <div class="content-panel">
                  <div class="panel-title">
                    <h3>{{ $t('hotelCancellationProtection') }}</h3>
                  </div>
                  <div class="panel-body" v-if="insuranceData">
                    <div class="upsell-panel">
                      <div class="content">
                        <i class="fal fa-hand-holding-medical"></i>

                        <div class="text">
                          <p>{{ $t('hotelCancellationProtectionParagraph') }}</p>
                        </div>
                      </div>

                      <div class="actions">
                        <div class="passenger-selection">
                          <ul class="options">
                            <li>
                              <input type="radio" v-model="bookData.addInsurance" :value="true" id="addInsurance">
                              <label for="addInsurance">
                                <span>{{ $t('hotelCancellationAddedParagraph') }}</span>
                                <span class="price">
                                  {{insuranceData.default.display_price | formatCurrency(insuranceData.default.display_currency, 2) }}
                                </span>
                              </label>
                            </li>
                            <li>
                              <input type="radio" v-model="bookData.addInsurance" :value="false" id="declineInsurance">
                              <label for="declineInsurance">
                                <span>{{ $t('hotelCancellationDeclinedParagraph') }}</span>
                              </label>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="panel-body loading" v-else>
                    <i class="fal fa-spinner-third fa-spin"></i> {{ $t('loadingDetails') }}
                  </div>
                </div>
              </section>

              <section class="hotel-booking-section">
                <div class="content-panel">
                  <div class="panel-title">
                    <h3>{{ $t('contactInformation') }}</h3>
                  </div>
                  <div class="panel-body">
                    <div class="two-col-form">
                      <div class="form-group">
                        <label for="">{{ $t('firstName') }}</label>
                        <input type="text" :name="$t('firstName')" v-validate="'required'" v-model="bookData.firstName" />
                        <span class="invalid">{{ errors.first($t('firstName')) }}</span>
                      </div>

                      <div class="form-group">
                        <label for="">{{ $t('lastName') }}</label>
                        <input type="text" :name="$t('lastName')" v-validate="'required'" v-model="bookData.lastName" />
                        <span class="invalid">{{ errors.first($t('lastName')) }}</span>
                      </div>

                      <div class="form-group">
                        <label for="">{{ $t('emailAddress') }}</label>
                        <input type="email" :name="$t('emailAddress')" v-validate="'required|email'" v-model="bookData.email" />
                        <span class="invalid">{{ errors.first($t('emailAddress')) }}</span>
                      </div>

                      <div class="form-group">
                        <label for="">{{ $t('phoneNumber') }}</label>
                        <input type="text" :name="$t('phoneNumber')" v-validate="'required|numeric'" v-model="bookData.phone" />
                        <span class="invalid">{{ errors.first($t('phoneNumber')) }}</span>
                      </div>

                      <div class="form-group">
                        <label for="">{{ $t('address') }}</label>
                        <input type="text" :name="$t('address')" v-validate="'required'" v-model="bookData.address" />
                        <span class="invalid">{{ errors.first($t('address')) }}</span>
                      </div>

                      <div class="form-group">
                        <label for="">{{ $t('city') }}</label>
                        <input type="text" :name="$t('city')" v-validate="'required'" v-model="bookData.city" />
                        <span class="invalid">{{ errors.first($t('city')) }}</span>
                      </div>

                      <div class="form-group">
                        <label for="">{{ $t('country') }}</label>
                        <v-select
                            v-validate="'required'"
                            :options="availResult.allowed_countries_data"
                            v-model="bookData.country"
                            class="country-code"
                            label="name"
                            :reduce="country => { return country.code}"
                            :name="$t('country')">
                        </v-select>
                        <span class="invalid">{{ errors.first($t('country')) }}</span>
                      </div>

                      <div class="form-group" v-if="bookData.country === '' || (getRegionsList && getRegionsList.length > 0)">
                        <label for="">{{ $t('provinceState') }}</label>
                        <v-select
                            v-validate="'required'"
                            :options="getRegionsList"
                            v-model="bookData.stateProvince"
                            class="country-code"
                            label="name"
                            :reduce="region => { return region.code}"
                            :name="$t('provinceState')">
                        </v-select>
                        <span class="invalid">{{ errors.first($t('provinceState')) }}</span>
                      </div>

                      <div class="form-group">
                        <label for="">{{ $t('postalZipCode') }}</label>
                        <input type="text" class="input-half" v-validate="'required'" :name="$t('postalZipCode')" v-model="bookData.zipPostal" />
                        <span class="invalid">{{ errors.first($t('postalZipCode')) }}</span>
                      </div>
                    </div>

                  </div>
                </div>
              </section>

              <section class="hotel-booking-section">
                <div class="content-panel">
                  <div class="panel-title">
                    <h3>{{ $t('paymentInformation') }}</h3>
                  </div>
                  <div class="panel-body">
                    <div class="two-col-form payment-types">
                      <div class="form-group">
                        <label for="">{{ $t('cardType') }}:</label>
                        <v-select
                          :name="$t('cardType')"
                          v-validate="'required'"
                          :options="[
                            { label: 'Visa', code: 'VI' },
                            { label: 'MasterCard', code: 'MC' },
                            { label: 'American Express', code: 'AX' },
                            { label: 'Discover', code: 'DS' }
                          ]"
                          :reduce="label => label.code"
                          v-model="bookData.ccType">
                        </v-select>
                        <span class="invalid">{{ errors.first($t('cardType')) }}</span>
                      </div>
                    </div>

                    <div class="two-col-form">

                      <div class="form-group">
                        <label for="">{{ $t('cardholderName') }}:</label>
                        <input type="text" :name="$t('cardholderName')" v-validate="'required'" v-model="bookData.ccHolder" />
                        <span class="invalid">{{ errors.first($t('cardholderName')) }}</span>
                      </div>

                      <div class="form-group">
                        <label for="">{{ $t('cardNumber') }}:</label>
                        <input type="text" :name="$t('cardNumber')" v-validate="'required|numeric'" v-model="bookData.ccNumber" />
                        <span class="invalid">{{ errors.first($t('cardNumber')) }}</span>
                      </div>

                      <div class="form-group">
                        <label for="">{{ $t('expiry') }}:</label>
                        <div class="text-input-half">
                          <input type="number" placeholder="MM" :name="$t('month')" max="12" min="1" v-validate="'required'" v-model="bookData.ccExpiryMonth" maxlength="2" minlength="2">
                          <input type="number" placeholder="YYYY" :name="$t('year')" v-validate="'required'" v-model="bookData.ccExpiryYear" maxlength="4" minlength="4">
                        </div>
                        <span class="invalid">{{ errors.first($t('month')) }}</span>
                        <span class="invalid">{{ errors.first($t('year')) }}</span>
                      </div>

                      <div class="form-group">
                        <label for="">{{ $t('cvv') }}:</label>
                        <input class="input-half" type="text" v-validate="'required|numeric'" :name="$t('cvv')" v-model="bookData.ccCvv"/>
                        <span class="invalid">{{ errors.first($t('cvv')) }}</span>
                      </div>

                    </div>
                  </div>
                </div>
              </section>

              <section class="hotel-booking-section">
                <div class="content-panel">
                  <div class="panel-title">
                    <h3>{{ $t('importantInformation') }}</h3>
                  </div>
                  <div class="panel-body fixed-height">
                    <div class="policy-info" v-for="(policy, policyIndex) in policyData" :key="policyIndex" v-if="policy.title !== 'Travel Insurance (Trip Protection)'">
                      <h5>{{policy.title}}</h5>
                      <p v-for="(policyChild, policyChildIndex) in policy.paragraph_data" :key="policyChildIndex" v-html="policyChild.replace('#START_PHOTO#', '').replace('#END_PHOTO#', '').split('#br#').join( '<br/>')"></p>
                    </div>
                  </div>
                </div>
              </section>

              <section class="content-panel charges-summary mobile-charges-summary">
                <div class="panel-title toggle" @click="showMobileSummary = !showMobileSummary">
                  <h3>{{ $t('summaryOfCharges') }}</h3>
                  <div class="flight-details-toggle">
                    <span v-if="showMobileSummary"><i class="fal fa-minus"></i></span>
                    <span v-else><i class="fal fa-plus"></i></span>
                  </div>
                </div>
                <div class="panel-body" v-if="showMobileSummary">

                    <h5>{{ $t('roomSummary') }}: <br />

                      <span v-if="hotelData.room_data[0].rate_data[0].description">
                        {{ hotelData.room_data[0].rate_data[0].description}}
                      </span>
                      <span v-else>{{ hotelData.room_data[0].rate_data[0].title }}</span><br />

                      <strong>{{ $t('datesOfStay') }}:</strong> {{ $moment(inputData.check_in).format('ddd, MMM DD') }} to {{ $moment(inputData.check_out).format('ddd, MMM DD') }}
                    </h5>

                    <div class="passenger-summary">
                      <div class="summary" v-for="(_, index) in new Array(parseInt(inputData.num_nights))" :key="index">
                        <div class="label">
                          {{ $moment(inputData.check_in).add(index, 'days').format('dddd, MMM DD') }}
                        </div>
                        <div class="value">
                          {{ hotelData.room_data[0].rate_data[0].price_details.night_price_data[index].display_night_price | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                        </div>
                      </div>
                      <div class="summary">
                        <div class="label">{{ $t('subtotal') }}</div>
                        <div class="value">
                          {{ hotelData.room_data[0].rate_data[0].price_details.display_sub_total | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                        </div>
                      </div>
                      <div class="summary" v-if="bookData.addInsurance">
                        <div class="label">{{ $t('hotelCancellationProtection') }}</div>
                        <div class="value">
                          {{ hotelData.room_data[0].rate_data[0].price_details.display_insurance_fee | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                        </div>
                      </div>
                      <div class="summary">
                        <div class="label">{{ $t('taxes') }}</div>
                        <div class="value">
                          {{ hotelData.room_data[0].rate_data[0].price_details.display_taxes | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                        </div>
                      </div>
                      <div class="summary" v-if="hotelData.room_data[0].rate_data[0].price_details.display_property_fee !== 0">
                        <div class="label">{{ $t('resortFee') }}</div>
                        <div class="value">
                          {{ hotelData.room_data[0].rate_data[0].price_details.display_property_fee | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                        </div>
                      </div>
                      <div class="summary total">
                        <div class="label">{{ $t('totalDueNow') }}</div>
                        <div class="value">
                            {{ totalPrice | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                        </div>
                      </div>

                      <div class="summary total" v-if="hotelData.room_data[0].rate_data[0].price_details.display_property_fee !== 0">
                        <div class="label">{{ $t('totalDueAtHotel') }}</div>
                        <div class="value">
                            {{ hotelData.room_data[0].rate_data[0].price_details.display_property_fee | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                        </div>
                      </div>
                    </div>


                </div>
              </section>

              <div class="accept-terms-check">
                <input type="checkbox" id="acceptTermsCheck" v-model="acceptTermsCheck" />

                <label for="acceptTermsCheck">
                  {{ $t('acceptHotelTermsParagraph') }}
                  <a href="/terms" :title="$t('viewTermsAndConditions')" target="_blank">{{ $t('viewTermsAndConditions') }}</a>
                </label>
              </div>

              <p class="form-error" v-if="showFieldErrorMessage">
                {{ $t('bookingFormFieldsErrorParagraph')}}
              </p>

              <div class="book-cta">
                  <a class="back" @click="$router.go(-1)"><i class="fal fa-angle-left"></i> {{ $t('backToPropertyPage') }}</a>

                  <button class="btn btn-lg" v-if="acceptTermsCheck" @click="submitBooking()">
                    <span>{{ $t('bookNow') }}</span>
                  </button>
                  <span v-else-if="!acceptTermsCheck" :v-tooltip="$t('pleaseAcceptTheTermsAndConditionsAbove')">
                    <button disabled="disabled" class="btn btn-sec btn-lg disabled">
                      <span>{{ $t('bookNow') }}</span>
                    </button>
                  </span>
              </div>

            </div>

          </div>

          <aside v-if="!bookLoading" class="summary-col">
            <div class="sticky">
              <section class="aside-panel charges-summary">
                <h4>{{ $t('summaryOfCharges') }}</h4>
                <div v-if="hotelData">
                  <h5>{{ $t('roomSummary') }}: <br />

                    <span v-if="hotelData.room_data[0].rate_data[0].description">
                      {{ hotelData.room_data[0].rate_data[0].description}}
                    </span>
                    <span v-else>{{ hotelData.room_data[0].rate_data[0].title }}</span><br />

                    <strong>{{ $t('datesOfStay') }}:</strong> {{ $moment(inputData.check_in).format('ddd, MMM DD') }} to {{ $moment(inputData.check_out).format('ddd, MMM DD') }}
                  </h5>

                  <div class="passenger-summary">
                    <div class="summary" v-for="(_, index) in new Array(parseInt(inputData.num_nights))" :key="index">
                      <div class="label">
                        {{ $moment(inputData.check_in).add(index, 'days').format('dddd, MMM DD') }}
                      </div>
                      <div class="value">
                        {{ hotelData.room_data[0].rate_data[0].price_details.night_price_data[index].display_night_price | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                      </div>
                    </div>
                    <div class="summary">
                      <div class="label">{{ $t('subtotal') }}</div>
                      <div class="value">
                        {{ hotelData.room_data[0].rate_data[0].price_details.display_sub_total | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                      </div>
                    </div>
                    <div class="summary" v-if="bookData.addInsurance">
                      <div class="label">{{ $t('hotelCancellationProtection') }}</div>
                      <div class="value">
                        {{ hotelData.room_data[0].rate_data[0].price_details.display_insurance_fee | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                      </div>
                    </div>
                    <div class="summary">
                      <div class="label">{{ $t('taxes') }}</div>
                      <div class="value">
                        {{ hotelData.room_data[0].rate_data[0].price_details.display_taxes | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                      </div>
                    </div>
                    <div class="summary" v-if="hotelData.room_data[0].rate_data[0].price_details.display_property_fee !== 0">
                      <div class="label">{{ $t('resortFee') }}</div>
                      <div class="value">
                        {{ hotelData.room_data[0].rate_data[0].price_details.display_property_fee | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                      </div>
                    </div>
                    <div class="summary total">
                      <div class="label">{{ $t('totalDueNow') }}</div>
                      <div class="value">
                          {{ totalPrice | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                      </div>
                    </div>

                    <div class="summary total" v-if="hotelData.room_data[0].rate_data[0].price_details.display_property_fee !== 0">
                      <div class="label">{{ $t('totalDueAtHotel') }}</div>
                      <div class="value">
                          {{ hotelData.room_data[0].rate_data[0].price_details.display_property_fee | formatCurrency(hotelData.room_data[0].rate_data[0].price_details.display_currency, 2) }}
                      </div>
                    </div>
                  </div>

                </div>
                <div class="loading" v-else>
                  <i class="fal fa-spinner-third fa-spin"></i>
                  <span>{{ $t('loadingSummary') }}</span>
                </div>
              </section>

              <section class="aside-panel reasons-to-book">
                <h4>{{ $t('includedWithYourBooking') }}:</h4>

                <ul class="checks">
                  <li>
                    <i class="fal fa-check"></i>
                    <span class="text">
                      <strong>{{ $t('theCheapestHotels') }}</strong>
                      {{ $t('cheapestHotelsParagraph') }}
                    </span>
                  </li>
                  <li>
                    <i class="fal fa-check"></i>
                    <span class="text">
                      <strong>{{ $t('excellentCustomerService') }}</strong>
                      {{ $t('customerServiceParagraph') }}
                    </span>
                  </li>
                  <li>
                    <i class="fal fa-check"></i>
                    <span class="text">
                      <strong>{{ $t('secureTransactions') }}</strong>
                      {{ $t('secureTransactionsParagraph') }}
                    </span>
                  </li>
                </ul>
              </section>
            </div>
          </aside>
        </div>

      </div>
    </div>
  </div>
</template>

<script>

import LoadingWidgetReasons from '@/components/LoadingWidgetReasons';
import HotelsApi from "../../services/hotelsApi";
const hotelsApi = new HotelsApi();
import HotelListing from '@/components/hotels/HotelListing';
import LoadingHotelListing from '@/components/hotels/LoadingHotelListing';
import { mapGetters } from 'vuex';
import {analytics} from "@/firebaseConfig";

export default {
  name: 'HotelBooking',
  data () {
    let self = this;
    return {
      api: {},
      title: 'Book Your Hotel',
      searchResults: {},
      showFieldErrorMessage: false,
      ppn: this.$route.params.ppn,
      availResult: {},
      insuranceData: null,
      //addInsurance: false,
      hotelData: null,
      inputData: null,
      bookLoading: false,
      acceptTermsCheck: false,
      bookSuccess: false,
      bookFailed: false,
      showBookFailedMsg: false,
      bookData: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        stateProvince: '',
        zipPostal: '',
        ccHolder: '',
        ccNumber: '',
        ccExpiryMonth: '',
        ccExpiryYear: '',
        ccType: '',
        ccCvv: '',
        addInsurance: false
      },
      contactInfo: { "emailAddress": "", "countryInfo": { "dialCode": 1, "code": "US" }, "phoneNumber": "" },
      widgetReasons: [
        {
          title: self.$t('theCheapestHotels'),
          description: self.$t('1MillionPropertiesWorldwide'),
          icon: 'fal fa-hotel'
        },
        {
          title: self.$t('247Support'),
          description: self.$t('emailChatOrCallAnytime'),
          icon: 'fal fa-user-headset'
        },
        {
          title: self.$t('secureTransactions'),
          description: self.$t('encryptedDataProtection'),
          icon: 'fal fa-lock-alt'
        },
        {
          title: self.$t('freeCancellation'),
          description: self.$t('cancelWithoutChargeOnManyProperties'),
          icon: 'fal fa-check'
        }
      ],
      paymentResult: {},
      confirmationData: {},
      confirmationCode: '',
      bookingStatus: '',
      itineraryData: {},
      customerData: {},
      allRoomsData: [],
      insuranceDetails: {},
      contractData: {},
      policyData: [],
      showMobileSummary: false
    }
  },
  metaInfo() {
    let self = this;
    return {
      title: self.$t('bookYourHotelStay'),
    }
  },
  components: {
    HotelListing,
    LoadingHotelListing,
    LoadingWidgetReasons
  },
  watch: {
    'bookData.country': function () {
      let self = this;
      self.bookData.stateProvince = '';
    }
  },
  mounted () {
    let self = this;
    if (self.$route.query.debug) {
      self.bookData = {
        firstName: 'Something',
        lastName: 'Other',
        email: '',
        phone: '4324324324',
        address: '123 Other Street',
        city: 'Toronto',
        country: 'CA',
        stateProvince: 'ON',
        zipPostal: 'M1M1M1',
        ccHolder: 'Something Other',
        ccNumber: '4111111111111111',
        ccExpiryMonth: '01',
        ccExpiryYear: '2022',
        ccType: 'VI',
        ccCvv: '199',
        addInsurance: false
      };
    }
    hotelsApi.availability(self.ppn).then((result) => {
      self.availResult = result.data['getHotelExpress.Contract'].results;
      self.hotelData = self.availResult.hotel_data[0];
      self.insuranceData = self.hotelData.room_data[0].rate_data[0].upsell_data.travel_insurance;
      self.inputData = self.availResult.input_data;
      self.policyData = self.hotelData.room_data[0].rate_data[0].policy_data;
      analytics.logEvent('availability', {agency: window.TravelPlatform.key, type: 'hotel', id: self.hotelData.id, name: self.hotelData.name, city: self.hotelData.address.city_name, state: self.hotelData.address.state_name});
    })
    .catch((err) => {
      console.log(err);
    });
  },
  computed: {
    ...mapGetters ([
      'getSampleHotelData',
      'getHotelResult'
    ]),

    getRegionsList: function () {
      let self = this;
      if (self.bookData.country.length > 0) {
        let country = self.availResult.allowed_countries_data.find(country => country.code === self.bookData.country);
        return country.region_data;
      }

      return [];
    },

    totalPrice: function () {
      let self = this;
      let totalPrice = parseFloat(self.hotelData.room_data[0].rate_data[0].price_details.display_total);
      if (self.bookData.addInsurance) {
        totalPrice += parseFloat(self.insuranceData.default.display_price);
      }
      return totalPrice;
    }
  },
  methods: {
    submitBooking () {
      let self = this;
      self.$validator.validateAll().then((result) => {
        if (result) {
          self.bookLoading = true;
          hotelsApi.book(self.availResult.bundle_data.ppn_book_bundle, self.bookData).then((results) => {
            self.paymentResult = results.data;
            self.bookingStatus = self.paymentResult['getHotelExpress.Book'].results.book_data.itinerary.status;

            if(self.bookingStatus === 'Confirmed') {
              let bookingId = self.paymentResult['getHotelExpress.Book'].results.book_data.itinerary.id;

              self.$router.push({
                name: 'EditHotelBooking',
                params: { bookingId: bookingId, email: self.bookData.email},
                query: { isConfirmation: true }
              });

              analytics.logEvent('bookingComplete', {agency: window.TravelPlatform.key, type: 'hotel', id: self.hotelData.id, name: self.hotelData.name, city: self.hotelData.address.city_name, state: self.hotelData.address.state_name, country: self.hotelData.address.country_name, price: self.totalPrice});

            } else {
              self.bookFailed = true;
              self.bookSuccess = false;
              self.bookLoading = false;
            }
          }).catch(function(err) {
            self.bookFailed = true;
            self.bookSuccess = false;
            self.bookLoading = false;
            if (err) {
              self.showBookFailedMsg = true;
            }
          });
        } else {
          self.showFieldErrorMessage = true;
          self.bookLoading = false;
        }
      });
    },
  }
}
</script>
